import { bbox, bboxClip } from "@turf/turf";
import { SettingsManager } from "@viamap/viamap2-common";
import { PDFDocument, PDFPage, rgb } from "pdf-lib";
import { splitIntoParts } from "src/components/DatarapportShared";
import { GenerateGeomUtils } from "src/managers/GenerateGeomUtils";

type Block = { left: number, top: number, width: number, height: number };
export class MITDocument {
   pdf: PDFDocument;
   currentPage: PDFPage | null = null;
   currentBlock: Block | null = null;
   elements: MITDocumentElement[] = [];
   header: MITDocumentHeader | null = null;
   footer: MITDocumentFooter | null = null;
   showBounds: boolean = false;

   title: string = "";
   mVersion: string = "";
   date: Date = new Date();
   pageNr: string = "{i} / {t}";

   constructor(pdf: PDFDocument) {
      this.pdf = pdf;
      return this;
   }

   async save() {
      await this._writePDF();
      return this.pdf.save();
   }

   debug() {
      this.showBounds = true;
   }

   add<T extends MITDocumentElement>(element: T): T {
      this.elements.push(element);
      return element;
   }

   setTitle(arg: string) { this.title = arg; return this; }
   setMVersion(arg: string) { this.mVersion = arg; return this; }
   setDate(arg: Date) { this.date = arg; return this; }
   setPageNr(arg: string) { this.pageNr = arg; return this; }

   nextPage(): Block {
      let page = this.pdf.addPage();

      this.currentPage = page;
      this.currentBlock = { left: 30, top: 796, width: 595 - 60, height: 800 - 30 };
      return this.currentBlock;
   }

   async _writePDF() {
      this.nextPage();
      for (const element of this.elements) {
         let blokE = await element.render(this);
         this.currentBlock!.top -= blokE.height;
         this.currentBlock!.height -= blokE.height;
      }


      let dateString = Intl.DateTimeFormat("da-dk", { dateStyle: "short", timeStyle: undefined }).format(new Date());
      this.pdf.getPages().forEach((page, x) => {
         let topY = page.getSize().height - 30;
         if (x == 0) {
            topY = 36;
         }

         page.setFontSize(8);

         page.drawText(this.title, { x: 30, y: topY });
         page.drawText("Mapit v" + this.mVersion, { x: 30, y: topY - 8 });
         let oYDate = Utils.measureTextWidth(dateString, 8);
         page.drawText(dateString, { x: 595 - 30 - oYDate, y: topY });
         let pageText = this.pageNr.replace("{i}", String(x + 1)).replace("{t}", String(this.pdf.getPages().length));
         let oYPage = Utils.measureTextWidth(pageText, 8);
         page.drawText(pageText, { x: 595 - 30 - oYPage, y: topY - 8 });
      });
   }

}

class MITDocumentElement {
   showBounds: boolean = false;
   parent: MITDocumentElement | null = null;

   constructor(parent?: MITDocumentElement) {
      this.parent = parent ?? null;
      return this;
   }

   notHeight(doc: MITDocument, usedHeight: number, requiredHeight: number): boolean {
      if ((doc.currentBlock?.height || 0) - usedHeight >= requiredHeight) {
         return false;
      }
      this.nextBlock(doc);
      return true;
   }

   nextBlock(doc: MITDocument): Block {
      let x = this.parent?.nextBlock(doc);
      if (!x) {
         return doc.nextPage();
      }
      return x;
   }

   // oxlint-disable-next-line no-unused-vars
   async render(doc: MITDocument): Promise<Block> {
      return { left: 0, top: 0, width: 0, height: 0 };
   }
}
export class MITDocumentSquare extends MITDocumentElement {
   _w: number;
   _h: number;

   constructor(width: number, height: number) {
      super();
      this._w = width;
      this._h = height;
      return this;
   }

   async render(doc: MITDocument): Promise<Block> {
      return { left: doc.currentBlock?.left ?? 0, top: (doc.currentBlock?.top ?? 0) - this._h, width: this._w, height: Math.min(this._h, doc.currentBlock?.height ?? this._h) };
   }
}

export class MITDocumentNextPage extends MITDocumentElement {
   async render(doc: MITDocument): Promise<Block> {
      doc.nextPage();
      return { left: 0, top: 0, width: 0, height: 0 };
   }
}
export class MITDocumentText extends MITDocumentElement {
   text: string;
   textSize: number = 12;
   lineHeight: number = 1.2;

   constructor(text: string) {
      super();
      this.text = text;
      return this;
   }

   setTextSize(size: number) {
      this.textSize = size;
      return this;
   }

   async render(doc: MITDocument): Promise<Block> {
      let block = doc.currentBlock ?? this.nextBlock(doc);
      if (block.height <= 0 || block.top < 0) {
         block = this.nextBlock(doc);
      }

      let page = doc.currentPage;
      if (!page) {
         throw new Error("No current page to render on");
      }
      let textA = this.text.split(" ");
      let rllTop = block.top;
      let maxWidth = 0;
      splitIntoParts(textA, block.width, this.fnWidth.bind(this)).forEach((line) => {
         maxWidth = Math.max(maxWidth, this.fnWidth(line));
         if (rllTop < (block.top - block.height)) {
            block = doc.currentBlock ?? this.nextBlock(doc);
            page = doc.currentPage ?? doc.pdf.addPage();
            rllTop = block.top;
         }
         page!.drawText(line.join(" "), { x: block.left, y: rllTop - this.textSize, size: this.textSize });
         rllTop -= this.lineHeight * this.textSize;
      });
      return { left: block.left, top: block.top - (block.top - rllTop), width: block.width, height: block.top - rllTop };
   }

   fnWidth(tArr: string[]) {
      return Utils.measureTextWidth(tArr.join(" "), this.textSize);
   }
}

export class MITDocumentTable extends MITDocumentElement {
   textSize: number = 10;
   data: { [key: string]: any }[];

   constructor(data: { [key: string]: any }[]) {
      super();
      this.data = data;
      this.textSize = 10;
      return this;
   }


   async render(doc: MITDocument): Promise<Block> {
      let block = doc.currentBlock ?? this.nextBlock(doc);

      if (this?.data.length === 0) {
         return { left: block.left, top: block.top, width: block.width, height: 0 };
      }

      let dataWH = [(Object.fromEntries(Object.keys(this.data[0] ?? {}).map(k => [k, k]))), ...this.data];
      let parts = Math.min(Object.keys(this.data[0] ?? {}).length || 1);

      // let colWidths = block.width / parts;
      let page = doc.currentPage;

      let colWidths = calcWidths(dataWH, parts);

      // 
      let maxColumns = findMaximumColumns(colWidths, block.width);
      colWidths = colWidths.slice(0, maxColumns);


      let total = colWidths.reduce((acc, w) => acc + w, 0);

      let scale = block.width / total;
      let scaledColWidths = colWidths.map(w => w * scale);

      let rllTop = block.top
      for (let i = 0; i < dataWH.length; i++) {
         let row = dataWH[i];
         if (this.notHeight(doc, block.top - rllTop, this.textSize * 1.2)) {
            block = doc.currentBlock ?? this.nextBlock(doc);
            page = doc.currentPage ?? doc.pdf.addPage();
            rllTop = block.top;
         }
         if (!page) {
            throw new Error("No current page to render on");
         }
         let left = block.left;
         Object.keys(row).forEach((key, idx) => {
            if (idx >= parts || idx >= maxColumns) {
               return;
            }
            left += scaledColWidths[idx - 1] || 0;
            let text = String(row[key]);
            page!.drawText(text, { x: left, y: rllTop - this.textSize, size: this.textSize });
         });
         rllTop -= this.textSize * 1.2;
      }
      return {
         left: block.left,
         top: block.top - (block.top - rllTop),
         width: block.width,
         height: (block.top - rllTop)
      }
   }
}

export class MITDocumentVecMap extends MITDocumentElement {
   transformer: null | { rToS: (coord: [number, number]) => [number, number], sToR: (coord: [number, number]) => [number, number] };
   geojsonArr: any[];
   sig: { title: string, style?: any }[] = [];
   imgQuality: number = 0;

   constructor(geojson: any[] | any) {
      super();
      this.transformer = null;
      if (!Array.isArray(geojson)) {
         geojson = [geojson];
      }
      this.geojsonArr = geojson;
      return this;
   }

   createTransformer(geoJ: any, block: Block) {
      let projectedGeo = this.projectToDTK(geoJ);
      let [minX, minY, maxX, maxY] = bboxBuffer(bbox(projectedGeo), 10);

      let deltaX = maxX - minX;
      let deltaY = maxY - minY;

      let scale = Math.min(block.width / deltaX, block.height / deltaY) * 0.9;
      let offsetX = (block.width - deltaX * scale) / 2;
      let offsetY = (block.height - deltaY * scale) / 2;

      return {
         rToS: (coord: [number, number]) => {
            let [cX, cY,] = coord;
            return [
               (cX - minX) * scale + offsetX,
               (maxY - cY) * scale + offsetY,
            ] as [number, number];
         },
         sToR: (coord: [number, number]) => {
            let [cX, cY] = coord;
            // should be inverse of above
            return [
               (cX - offsetX) / scale + minX,
               maxY - (cY - offsetY) / scale,
            ] as [number, number];
         }
      }
   }

   setImage(nr: number) {
      this.imgQuality = nr;
      return this;
   }

   setSignature(sig: { title: string, style?: any }[]) {
      this.sig = sig;
      return this;
   }

   async render(doc: MITDocument): Promise<Block> {
      let blovk = doc.currentBlock ?? this.nextBlock(doc);
      let transform = this.transformer ?? this.createTransformer(this.geojsonArr[0], blovk);

      if (this.imgQuality > 0) {
         let bottomRight = transform.sToR([blovk.width, 0]);
         let topLeft = transform.sToR([0, blovk.height]);
         let blob = await fetchImage(topLeft.concat(bottomRight) as [number, number, number, number], Math.round(blovk.width * this.imgQuality), Math.round(blovk.height * this.imgQuality))
         let image = await doc.pdf.embedJpg(blob);
         doc.currentPage?.drawImage(image, {
            x: blovk.left,
            y: blovk.top - blovk.height,
            width: blovk.width,
            height: blovk.height,
            opacity: 0.4,
         })
      }
      this.geojsonArr?.forEach(geoJ => {
         blovk = this.renderGj(doc, geoJ, transform);
      });

      if (this.sig.length) {
         doc.currentPage?.drawRectangle({ x: blovk.left, y: blovk.top + this.sig.length * 15 + 2, width: blovk.width / 4, height: -2 - this.sig.length * 15, color: rgb(1, 1, 1), borderColor: rgb(0.9, 0.9, 0.9), borderWidth: .1 });

         this.sig.forEach((s, idx) => {
            doc.currentPage?.drawSvgPath(`M 0 0 h 10 v 10 h -10 Z`, { x: blovk.left + 3, y: - 2 + blovk.top + this.sig.length * 15 - idx * 15, ...(s.style ?? { color: rgb(0, 0, 0) }) });
            doc.currentPage?.drawText(s.title, { x: blovk.left + 16, y: blovk.top + this.sig.length * 15 - idx * 15 - 10, size: 10 });
         });
      }
      doc.currentPage?.drawRectangle({ x: blovk.left, y: blovk.top, width: blovk.width, height: blovk.height, borderColor: rgb(0.9, 0.9, 0.9), borderWidth: .1 });
      return blovk;
   }

   renderGj(doc: MITDocument, geoJ: any, transform: NonNullable<typeof this.transformer>): Block {
      if (!geoJ?.features?.length) {
         return doc.currentBlock ?? this.nextBlock(doc);
      }

      let pdfStyleFC = geoJ.pdfStyle
      let block = doc.currentBlock ?? this.nextBlock(doc);

      let projectedGeo = this.projectToDTK(geoJ);


      projectedGeo.features.forEach((feature: any) => {
         let ft = GenerateGeomUtils.geoJsonCoordsTransformer(feature, (coord: [number, number, number?]) => {
            let [px, py] = coord;
            let [x, y] = transform.rToS([px, py]);
            return [x, y];
         });
         let pdfStyle = feature.pdfStyle ?? pdfStyleFC ?? null;
         let clippedFt = cBBOXClip(ft, [0, 0, block.width, block.height]);
         if (!clippedFt) {
            return;
         }
         featureToSvgPaths(clippedFt).forEach((path: string) => {
            if (path.startsWith("P")) {
               let [_P, x, y] = path.split(" ").map(Number);

               doc.currentPage?.drawCircle({ x: block.left + x, y: block.top - y, ...(pdfStyle ?? { size: 3, color: rgb(0.8, 0, 0) }) });
               return
            } else if (path.startsWith("L")) {
               path = path.replace("L", "M");
               doc.currentPage?.drawSvgPath(path, { x: block.left, y: block.top, ...(pdfStyle ?? { borderColor: rgb(0.8, 0, 0), borderWidth: 1, borderOpacity: 0.5 }) });
               return;
            }
            doc.currentPage?.drawSvgPath(path, { x: block.left, y: block.top, ...(pdfStyle ?? { color: rgb(0.8, 0, 0), opacity: 0.5 }) });
         });
      });

      return {
         left: block.left,
         top: block.top - block.height,
         width: block.width,
         height: block.height
      }
   }

   projectToDTK(geoJ: any) {
      return GenerateGeomUtils.geoJsonCoordsTransformer(geoJ, (coord: [number, number, number?]) => {
         let { x, y } = GenerateGeomUtils.convertFromLatLngToUTM({ lat: coord[1], lng: coord[0] });
         return [x, y];
      });

   }
}



export class MITDocumentTitle extends MITDocumentText {
   constructor(text: string) {
      super(text);
      this.textSize = 24;
      return this;
   }

   setTextSize(size: number) {
      this.textSize = size;
      return this;
   }

}

class MITDocumentHeader {
   constructor() {
      return this
   }
}
class MITDocumentFooter {

   constructor() {
      return this
   }
}
class Utils {
   static measureTextWidth(text: string, textSize: number): number {
      let context = document.createElement("canvas").getContext("2d");
      if (context) {
         context.font = `${textSize}px Arial`;
         return context.measureText(text).width;
      }
      return 0;
   }

   static splitLength(length: number, parts: number): number[] {
      let base = length / (parts || 0);
      return Array(parts).fill(base);
   }


}

function fetchImage(bbox: [number, number, number, number], width: number, height: number): Promise<ArrayBuffer> {
   let username = SettingsManager.getSystemSetting("datafordelerUsername")
   let password = SettingsManager.getSystemSetting("datafordelerPassword")
   let url = `https://wms.datafordeler.dk/GeoDanmarkOrto/orto_foraar/1.0.0/WMS?SERVICE=WMS&VERSION=1.3.0&REQUEST=GetMap&BBOX=${bbox.join(",")}&CRS=EPSG:25832&STYLES&FORMAT=image/jpeg&DPI=96&MAP_RESOLUTION=96&FORMAT_OPTIONS=dpi:96&Layers=orto_foraar&password=${password}&username=${username}&WIDTH=${width}&HEIGHT=${height}`
   return fetch(url)
      .then(res => res.arrayBuffer())
      .then(blob => {
         return blob;
      })
      .catch(err => {
         throw new Error("Failed to fetch image: " + err);
      });
}

function calcWidths(data: { [key: string]: any }[], parts: number): number[] {
   let widths: number[] = Array(parts).fill(0);
   for (let i = -1; i < parts; i++) {
      let arr = i === -1 ? Object.keys(data[0] ?? {}) : data.map(row => String(row[Object.keys(row)[i]]));
      arr.forEach((text) => {
         let w = Utils.measureTextWidth(text, 10);
         if (w > widths[i]) {
            widths[i] = w;
         }
      });
   }
   return widths;
}

function findMaximumColumns(col: number[], max: number): number {
   let acc = 0;
   for (let i = 0; i < col.length; i++) {
      if (acc + col[i] > max) {
         return i;
      }
      acc += col[i];
   }
   return col.length;
}

function bboxBuffer(bbox: [number, number, number, number, number?, number?], buffer: number): [number, number, number, number] {
   let [minX, minY, maxX, maxY, ..._] = bbox;
   return [minX - buffer, minY - buffer, maxX + buffer, maxY + buffer];
}

function cBBOXClip(feature: any, bbox: [number, number, number, number]): any {
   if (feature.geometry.type === "Point") {
      let [x, y] = feature.geometry.coordinates;
      let [minX, minY, maxX, maxY] = bbox;
      if (x >= minX && x <= maxX && y >= minY && y <= maxY) {
         return feature;
      }
      return null;
   } else if (feature.geometry.type === "MultiPoint") {
      let [minX, minY, maxX, maxY] = bbox;
      let points = feature.geometry.coordinates.filter(([x, y]: [number, number]) => x >= minX && x <= maxX && y >= minY && y <= maxY);
      if (points.length === 0) {
         return null;
      }
      return { ...feature, geometry: { ...feature.geometry, coordinates: points } };
   }
   return bboxClip(feature, bbox);
}

function featureToSvgPaths(feature: any): string[] {
   let geom = feature.geometry.type === "GeometryCollection" ? feature.geometry.geometries : feature.geometry;
   if (feature.geometry.type === "GeometryCollection") {
      return geom.map((g: any) => featureToSvgPaths({ ...feature, geometry: g })).flat();
   }
   if (geom.type === "Point") {
      return ["P " + geom.coordinates.join(" ")];
   }
   if (geom.type === "MultiPoint") {
      return geom.coordinates.map((c: any) => "P " + c.join(" "));
   }
   if (geom.type === "LineString") {
      return ["L" + geom.coordinates.map((c: any) => c.join(" ")).join(" L ")];
   }
   if (geom.type === "MultiLineString") {
      return geom.coordinates.map((line: any) => "L" + line.map((c: any) => c.join(" ")).join(" L "));
   }
   if (geom.type === "Polygon") {
      return [geom.coordinates.map((ring: any) => "M" + ring.map((c: any) => c.join(" ")).join(" L ") + " Z").join(" ")];
   }
   if (geom.type === "MultiPolygon") {
      return geom.coordinates.map((poly: any) => poly.map((ring: any) => "M" + ring.map((c: any) => c.join(" ")).join(" L ") + " Z").join(" "));
   }
   return [];
}